/**
 * What this workbook's pane is doing, which a recalculation it did not start must wait for, and when
 * its Bifrost cells were last set off to calculate. On globalThis because the functions bundle, whose
 * cells schedule recalculations of their own, and the task-pane bundle each load their own copy of
 * this module into the one runtime.
 */

interface PaneActivity {
  /** Pane actions running: recalculations, conversions to values, journal actions, the company switch. */
  running: number;
  /** When every Bifrost cell of the workbook was last set off; 0 for never. */
  allSetOffAt: number;
  /** When each sheet's Bifrost cells were last set off on their own, by sheet id. */
  sheetSetOffAt: Record<string, number>;
}

interface ActivityHolder {
  __bifrostPaneActivity?: PaneActivity;
}

function activity(): PaneActivity {
  const holder = globalThis as unknown as ActivityHolder;
  if (!holder.__bifrostPaneActivity) holder.__bifrostPaneActivity = { running: 0, allSetOffAt: 0, sheetSetOffAt: {} };
  return holder.__bifrostPaneActivity;
}

/** Runs a pane action, marked as running for as long as it does. Actions may nest. */
export async function duringPaneAction<T>(action: () => Promise<T>): Promise<T> {
  activity().running++;
  try {
    return await action();
  } finally {
    activity().running--;
  }
}

export function paneActionRunning(): boolean {
  return activity().running > 0;
}

/** Every Bifrost cell of this workbook has just been set off to calculate. */
export function allCellsSetOff(): void {
  activity().allSetOffAt = Date.now();
}

/** The Bifrost cells of these sheets have just been set off to calculate. */
export function sheetCellsSetOff(sheetIds: string[]): void {
  const now = Date.now();
  sheetIds.forEach((id) => { activity().sheetSetOffAt[id] = now; });
}

/** Whether every Bifrost cell of this workbook was set off at `moment` or later. */
export function allCellsSetOffSince(moment: number): boolean {
  const at = activity().allSetOffAt;
  return at > 0 && at >= moment;
}

/** Whether this sheet's Bifrost cells were set off at `moment` or later, on their own or with all the others. */
export function sheetCellsSetOffSince(sheetId: string, moment: number): boolean {
  const at = activity().sheetSetOffAt[sheetId] ?? 0;
  return allCellsSetOffSince(moment) || (at > 0 && at >= moment);
}
